export const PROFILE_IMAGE_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
] as const

export type ProfileImageMimeType = (typeof PROFILE_IMAGE_ALLOWED_MIME_TYPES)[number]

export const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024

export const PROFILE_IMAGE_INVALID_TYPE_ERROR =
  'Only JPG, PNG, or WEBP images are allowed.'

export const PROFILE_IMAGE_TOO_LARGE_ERROR = 'Image must be 5 MB or smaller.'

export function normalizeProfileImageMime(mime?: string | null): ProfileImageMimeType | null {
  if (!mime) return null
  const normalized = mime.toLowerCase().trim()
  if (normalized === 'image/jpg' || normalized === 'image/jpeg') return 'image/jpeg'
  if (normalized === 'image/png') return 'image/png'
  if (normalized === 'image/webp') return 'image/webp'
  return null
}

export function detectProfileImageMime(bytes: Uint8Array): ProfileImageMimeType | null {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg'
  }

  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47 &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png'
  }

  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp'
  }

  return null
}

export function getProfileImageExtension(mime: ProfileImageMimeType): string {
  switch (mime) {
    case 'image/png':
      return 'png'
    case 'image/webp':
      return 'webp'
    default:
      return 'jpg'
  }
}

export function validateProfileImageBytes(
  bytes: Uint8Array,
  declaredMime?: string | null
): { ok: true; mime: ProfileImageMimeType } | { ok: false; error: string } {
  if (!bytes.length) {
    return { ok: false, error: 'Selected image is empty. Please choose a different image.' }
  }

  if (bytes.length > PROFILE_IMAGE_MAX_BYTES) {
    return { ok: false, error: PROFILE_IMAGE_TOO_LARGE_ERROR }
  }

  const detectedMime = detectProfileImageMime(bytes)
  if (!detectedMime) {
    return { ok: false, error: PROFILE_IMAGE_INVALID_TYPE_ERROR }
  }

  const normalizedDeclared = normalizeProfileImageMime(declaredMime)
  if (normalizedDeclared && normalizedDeclared !== detectedMime) {
    return { ok: false, error: PROFILE_IMAGE_INVALID_TYPE_ERROR }
  }

  return { ok: true, mime: detectedMime }
}

export function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }

  return btoa(binary)
}

export async function fileToProfileImageBase64(file: File): Promise<{
  base64: string
  mime: ProfileImageMimeType
}> {
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
  const validation = validateProfileImageBytes(bytes, file.type)

  if (!validation.ok) {
    throw new Error(validation.error)
  }

  return {
    base64: uint8ArrayToBase64(bytes),
    mime: validation.mime,
  }
}
